/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { Box } from '@mui/material';
import * as moment from 'moment';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { debounce, isEmpty } from 'lodash';
import { Auth } from 'aws-amplify';

import {
  getAuthData,
  getAuthDetails,
  getStoredCustomerDetails,
  removeJobLocalData,
  getProcessData,
  hasEventId,
} from '../../../../Shared/Utilities/utils';
import { BooleanType } from '../../../../API';
import { USER_PROCESSES } from '../../../../Shared/Constants/App';
import {
  DateFormat,
  DateRangeOptionType,
  DefaultOptions,
  FilterName,
  FormFieldType,
  GraphTypes,
  UserType,
  OrgTypes,
  PROCESS_EVENTS,
  velocitiOrganizationId,
} from '../../../../Shared/Constants/App';

import AnalyticsApiService from '../../../../Shared/Services/AnalyticsService';
import JobCreateApiService from '../../../Jobs/Services/JobCreateService';
import CustomerApiService from '../../Services/CustomerService';
import FacilityApiService from '../../../Facility/Services/FacilityApiService';
import { GraphType } from '../../../../Shared/Components/Common/Graphs/Graphs.Model';

import Button, { ButtonType, IconDirection } from '../../../../Shared/Components/Common/Buttons/Button';
import GoogleMap from '../../../../Shared/Components/Common/GoogleMap/GoogleMap';
import { GraphTemplateV2 } from '../../../../Shared/Components/Common/GraphContainer/index';
import AsyncStorageKeys from '../../../../Shared/Constants/StorageKeys';
import { ACTION_TYPES } from '../../../../Shared/Store/actions/postActions';
import { INITIALSTATE, postReducer } from '../../../../Shared/Store/reducer/postReducer';
import ApproveDialog from '../../../Jobs/Components/TaskDetails/logoutDialog';
import AllJobs from '../../../Vendor/Components/Marketplace/AllJobs';

import { Icons } from '../../../../Shared/Constants/Icons';
import { WalkingLoader } from '../../../../Shared/Components/Common/MapLoader/MapLoader';
import theme from '../../../../Shared/Themes/theme';
import { HomePageContainer, SearchFieldContainer, SearchInput } from '../../../../Shared/Components/Layout/styles';
import { ButtonTitle, LoaderWrapper } from './Dashboard.style';
import {
  HeaderWrapper,
  Title,
  Wrapper,
} from '../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';

const HomePage = () => {
  const [showNoDataScreen, setShowNoDataScreen] = useState(false);
  const [state, dispatch] = useReducer(postReducer, INITIALSTATE);
  const [setNoShowGraphData] = useState({});
  const [isShowSessionExpirePopUp, setIsShowSessionExpirePopUp] = useState(false);
  const [isShowSessionExpirePopUpLoading, setIsShowSessionExpirePopUpLoading] = useState(false);
  const { loading, error, data } = state;
  const [allData, setAllData] = useState({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedGraph, setSelectedGraph] = useState('');
  const [jobCompletionTrendData, setJobCompletionTrendData] = useState([]);
  const [isJobCompletionTrendDataLoading, setIsJobCompletionTrendDataLoading] = useState(false);
  const [selectedAssociateId, setSelectedAssociateId] = useState();
  const [selectedFacilityId, setSelectedFacilityId] = useState();
  const [allAssociates, setAllAssociates] = useState([]);
  const [selectedAssociate, setSelectedAssociate] = useState('');
  const [selectedFacility, setSelectedFacility] = useState();
  const [facility, setFacility] = useState([]);
  const [isAssociateLoading, setIsAssociateLoading] = useState();
  const [isFacilityLoading, setIsFacilityLoading] = useState();
  const [selectedRoleBasedId, setSelectedRoleBasedId] = useState();
  const [customersAndVendors, setCustomersAndVendors] = useState([]);
  const [selectedCustomerVendor, setSelectedCustomerVendor] = useState('');
  const [isCustomerVendorLoading, setIsCustomerVendorLoading] = useState();
  const [searchString, setSearchString] = useState('');
  const [totalCounts, setTotalCounts] = useState(0);
  const [currentPages, setCurrentPages] = useState(1);
  const [processEvents, setProcessEvents] = useState([]);
  // Starts true: fetchProcessEvents runs on mount and always clears it, so the first render
  // doesn't show buttons and graphs before their permissions are known.
  const [isProcessEventsLoading, setIsProcessEventsLoading] = useState(true);

  const { t } = useTranslation(['homePage', 'oneTimeJob']);
  const navigate = useNavigate();
  const storedAuthDetails = getAuthDetails();
  const { role, isDistrictManager } = getAuthData();
  const locationData = { lat: 33.753746, long: -84.38633 }; //This is needed to set the initial center.
  const customRole = 'custom:Role';
  const roles = storedAuthDetails[customRole];
  const defaultDateRangeOption = '-7';
  const defaultRoleBasedOption = DefaultOptions.All;
  const { customerId = '', userId } = getStoredCustomerDetails() || {};

  const processData = getProcessData();

  const isCustomerOrNonVelocitiUsersLoggedIn =
    role === UserType.Customer || (role === UserType.Vendor && customerId !== velocitiOrganizationId);

  const dateRangeOptions = [
    { id: 1, label: t('dashboard:today'), value: '1', type: DateRangeOptionType.Days },
    { id: 2, label: t('dashboard:lastWeek'), value: '-7', type: DateRangeOptionType.Days },
    { id: 3, label: t('dashboard:lastMonth'), value: '-30', type: DateRangeOptionType.Days },
    { id: 4, label: t('dashboard:last45Days'), value: '-45', type: DateRangeOptionType.Days },
    { id: 5, label: t('dashboard:customDateRange'), value: 'custom', type: DateRangeOptionType.CustomDateRange },
  ];
  const labels = [
    { dataKey: 'missed', fill: theme.palette.secondary.customColor12, name: t('dashboard:missed') },
    { dataKey: 'scheduled', fill: theme.palette.secondary.customColor13, name: t('dashboard:scheduled') },
    { dataKey: 'completed', fill: theme.palette.secondary.customColor14, name: t('dashboard:completed') },
  ];

  const filters = [
    {
      title: storedAuthDetails[customRole] === UserType.Customer ? t('dashboard:vendors') : t('dashboard:associates'),
      type: FormFieldType.DropDown,
      name: FilterName.Associates,
      options: allAssociates.map((associateAndVendor) => ({
        label: associateAndVendor.name,
        value: associateAndVendor.id,
      })),
    },
    {
      title: t('dashboard:location'),
      type: FormFieldType.DropDown,
      name: FilterName.Location,
      options: facility.map((facility) => ({
        label: facility.name,
        value: facility.id,
      })),
    },
    {
      title: storedAuthDetails['custom:Role'] === UserType.Customer ? t('dashboard:vendors') : t('dashboard:customers'),
      type: FormFieldType.DropDown,
      name: FilterName.CustomerAndVendor,
      options: customersAndVendors.map((customersAndVendor) => ({
        label: customersAndVendor.name,
        value: customersAndVendor.id,
      })),
    },
  ];

  const getJobCompletionTrendData = async (
    startDate,
    endDate,
    selectedAssociateId = userId,
    selectedFacilityId = DefaultOptions.All,
    selectedRoleBasedId = ''
  ) => {
    try {
      setIsJobCompletionTrendDataLoading(true);
      const storedAuthDetails = getAuthDetails();
      const associateIdToUse = selectedAssociateId ?? userId;

      const jobCompletionData = await AnalyticsApiService.shiftTrend(
        customerId,
        startDate,
        endDate,
        storedAuthDetails[customRole] === UserType.Customer ? UserType.Customer : UserType.Vendor,
        userId,
        associateIdToUse,
        selectedFacilityId,
        selectedRoleBasedId
      );
      if (jobCompletionData?.data) {
        const modifiedData = jobCompletionData.data.map((dataItem) => {
          return {
            name: moment(dataItem.shiftDate).format(DateFormat.StandardDate),
            scheduled: dataItem.scheduled,
            missed: dataItem.missed,
            completed: dataItem.completed,
          };
        });

        setJobCompletionTrendData({
          title: t('dashboard:jobCompletionTrendByStatus'),
          xAxisLabel: t('dashboard:shiftDate'),
          yAxisLabel: t('dashboard:numberOfShifts'),
          data: modifiedData,
        });
      }
    } catch (e) {
      console.error(t('dashboard:ErrorFetchingJobCompletionTrendData'));
    } finally {
      setIsJobCompletionTrendDataLoading(false);
    }
  };

  const getNoShowGraphData = async (startDate, endDate) => {
    try {
      const storedAuthDetails = getAuthDetails();
      const { customerId = '', userId } = getStoredCustomerDetails() || {};
      const noShowFacilities = await AnalyticsApiService.noShowFacilities(
        customerId,
        startDate,
        endDate,
        storedAuthDetails[customRole] === UserType.Customer ? UserType.Customer : UserType.Vendor,
        userId
      );

      if (noShowFacilities?.data) {
        const modifiedData = noShowFacilities?.data?.map((dataItem) => {
          return {
            name: dataItem.facilityName,
            value: dataItem.count,
          };
        });

        setNoShowGraphData({
          title: 'Missed Jobs',
          graphView: 'vertical',
          graphColor: theme.palette.secondary.customColor3,
          data: modifiedData,
        });
      }
    } catch (e) {
      console.log('e', e);
    }
  };

  const fetchData = async (date) => {
    try {
      dispatch({ type: ACTION_TYPES.FETCH_START });
      const storedAuthDetails = getAuthDetails();
      const { customerId = '', userId } = getStoredCustomerDetails() || {};
      const allIdsResponse = await AnalyticsApiService.taskSummary(
        customerId,
        date,
        storedAuthDetails[customRole] === UserType.Customer ? UserType.Customer : UserType.Vendor,
        userId
      );

      if (allIdsResponse?.errors?.length) {
        dispatch({ type: ACTION_TYPES.FETCH_ERROR });
      } else if (typeof allIdsResponse?.data === 'object') {
        if (!Object.keys(allIdsResponse?.data)?.length) {
          dispatch({ type: ACTION_TYPES.FETCH_ERROR, payload: data });
          setShowNoDataScreen(true);
        } else if (Object.keys(allIdsResponse?.data)?.length) {
          setAllData(allIdsResponse?.data);
          dispatch({ type: ACTION_TYPES.FETCH_SUCCESS, payload: data });
        }
      }
    } catch (e) {
      console.log('error in catch', e);
      dispatch({ type: ACTION_TYPES.FETCH_ERROR });
    }
  };

  const handleFetchData = (date) => {
    void fetchData(date);
  };

  const onDateRangeSelect = (selectedRange, id, associatesId, facilityId, roleBasedId) => {
    let startDate = selectedRange.fromDate;
    let endDate = selectedRange.toDate;

    setFromDate(startDate);
    setToDate(endDate);
    setSelectedGraph(id);
    setSelectedAssociateId(associatesId ?? selectedAssociateId);
    setSelectedFacilityId(facilityId ?? selectedFacilityId);
    setSelectedRoleBasedId(roleBasedId ?? selectedRoleBasedId);
  };

  const handleAssociateChange = (associatesId, startDate, endDate) => {
    setSelectedAssociateId(associatesId);
    getJobCompletionTrendData(startDate, endDate, associatesId, selectedFacilityId, selectedRoleBasedId);
  };

  const handleCustomerVendorChange = (roleBasedId, startDate, endDate) => {
    setSelectedRoleBasedId(roleBasedId);
    void getJobCompletionTrendData(startDate, endDate, selectedAssociateId, selectedFacilityId, roleBasedId);
  };

  const handleLocationChange = (facilityId, startDate, endDate) => {
    setSelectedFacilityId(facilityId);
    void getJobCompletionTrendData(startDate, endDate, selectedAssociateId, facilityId, selectedRoleBasedId);
  };

  useEffect(() => {
    // const storedAuthDetails = getAuthDetails();
    localStorage.removeItem(AsyncStorageKeys.viewJob);
    localStorage.removeItem(AsyncStorageKeys.currentJob);
    localStorage.removeItem(AsyncStorageKeys.serviceRequestAttachments);
    localStorage.removeItem(AsyncStorageKeys.jobsListViewDateRange);
    removeJobLocalData();
  }, []);

  const signOut = useCallback(async () => {
    try {
      setIsShowSessionExpirePopUpLoading(true);
      await Auth.signOut();
      localStorage.removeItem('auth');
      localStorage.removeItem('customerDetails');
      navigate('/login');
    } catch (error) {
      console.error('An error occurred during sign out:', error);
    } finally {
      setIsShowSessionExpirePopUpLoading(false);
    }
  }, []);

  const LogoutUI = () => {
    return (
      <div>
        {t('logout:logoutConfirm')} <br /> <strong>{t('logout:peazyApplication')}</strong>
      </div>
    );
  };

  const fetchAllAssociates = async (currentPage, searchString = '') => {
    setIsAssociateLoading(true);
    try {
      const customerDetails = getStoredCustomerDetails();
      const vendorId = customerDetails?.customerId ? customerDetails?.customerId : '';
      if (!isEmpty(vendorId)) {
        const response = await CustomerApiService.getAllUsers(
          vendorId,
          '',
          currentPage,
          10,
          BooleanType.True,
          true,
          searchString
        );
        const associateData = response.data.map((associate) => ({
          id: associate.userId,
          name: associate.username,
        }));
        setTotalCounts(response?.metadata?.totalCount);
        setAllAssociates([...associateData]);
      }
    } catch (error) {
      console.error(t('dashboard:errorFetchingUsers'));
    } finally {
      setIsAssociateLoading(false);
    }
  };

  const debouncedFetchAllAssociates = useCallback(
    debounce((currentPages, searchString) => {
      fetchAllAssociates(currentPages, searchString);
    }, 1000),
    []
  );

  const handleSearchStringChange = (value) => {
    setSearchString(value);
  };

  const handlePaginationChanges = (event, value) => {
    setCurrentPages(value);
  };

  const fetchCustomersAndVendors = async () => {
    setIsCustomerVendorLoading(true);
    try {
      const customerDetails = getStoredCustomerDetails();
      let customerVendorData = [];
      let response;
      if (roles === UserType.Vendor) {
        const vendorId = customerDetails?.customerId;
        response = await JobCreateApiService.getVendorAffiliations(vendorId);
      } else if (roles === UserType.Customer) {
        const customerId = customerDetails?.customerId;
        response = await JobCreateApiService.getCustomerAffiliations(customerId);
      }

      customerVendorData = (response?.data || [])
        .filter((vendor) => vendor?.orgType === OrgTypes.Customer)
        .map((vendor) => ({
          id: vendor?.orgId,
          name: vendor?.orgName,
        }));
      setCustomersAndVendors([{ id: DefaultOptions.All, name: DefaultOptions.All }, ...customerVendorData]);
    } catch (error) {
      console.error(t('dashboard:errorFetchingUsers'), error);
    } finally {
      setIsCustomerVendorLoading(false);
    }
  };

  const handleAssociateChanges = (event) => {
    const value = event.target.value;
    setSelectedAssociate(value);
    handleAssociateChange(value);
  };

  const handleCustomerVendorChanges = (event) => {
    const value = event.target.value;
    setSelectedCustomerVendor(value);
    const id = value === DefaultOptions.All ? '' : value;
    handleCustomerVendorChange(id);
  };

  const fetchFacilities = async () => {
    setIsFacilityLoading(true);
    try {
      let facilitiesData = [];
      const { customerId = '' } = getStoredCustomerDetails() || {};
      const response = await FacilityApiService.getAllFacilities(customerId);
      facilitiesData = response.data.map((facility) => ({
        id: facility.facilityId,
        name: facility.buildingName,
      }));

      setFacility([{ id: DefaultOptions.All, name: DefaultOptions.All }, ...facilitiesData]);
    } catch (error) {
      console.error(t('dashboard:errorFetchingFacility'));
    } finally {
      setIsFacilityLoading(false);
    }
  };

  const handleFacilityChanges = (event) => {
    const value = event.target.value;
    setSelectedFacility(value);
    handleLocationChange(value);
  };

  useEffect(() => {
    if (!isEmpty(searchString?.trim()) && searchString?.length >= 3) {
      debouncedFetchAllAssociates(currentPages, searchString);
    } else if (isEmpty(searchString)) {
      debouncedFetchAllAssociates(currentPages, '');
    }
  }, [currentPages, searchString]);

  const fetchProcessEvents = async () => {
    try {
      setIsProcessEventsLoading(true);
      if (userId && customerId) {
        // Fetched together rather than one after the other.
        const [serviceRequestEventsResponse, homeEventsResponse] = await Promise.all([
          // Service Request access is granted via event 4507 under the Reports process (4500).
          // Fetch it for every organization type (customers, sub-vendors and internal users).
          CustomerApiService.fetchProcessEvents(userId, customerId, Number(USER_PROCESSES.Reports)),
          // Home process events drive the dashboard graph gating and are only relevant for internal users.
          isCustomerOrNonVelocitiUsersLoggedIn
            ? Promise.resolve({ data: [] })
            : CustomerApiService.fetchProcessEvents(userId, customerId, Number(USER_PROCESSES.Home)),
        ]);

        setProcessEvents([...(serviceRequestEventsResponse?.data || []), ...(homeEventsResponse?.data || [])]);
      }
      setIsProcessEventsLoading(false);
    } catch (error) {
      console.error('Error fetching process events:', error);
    } finally {
      setIsProcessEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomersAndVendors();
    fetchProcessEvents();
  }, []);

  useEffect(() => {
    void fetchFacilities();
  }, []);

  useEffect(() => {
    const startDate = moment().subtract(6, DateRangeOptionType.Days).format(DateFormat.ISODate);
    const endDate = moment().format(DateFormat.ISODate);
    // Below code is commented because noShow graph is not in use.
    // void getNoShowGraphData(startDate, endDate);
    getJobCompletionTrendData(startDate, endDate, selectedAssociateId, selectedFacilityId, selectedRoleBasedId);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      if (selectedGraph === GraphTypes.NoShows) {
        void getNoShowGraphData(fromDate, toDate);
      } else if (selectedGraph === GraphTypes.CompletionTrend) {
        void getJobCompletionTrendData(fromDate, toDate, selectedAssociateId, selectedFacilityId, selectedRoleBasedId);
      }
    }
  }, [fromDate, toDate, selectedGraph]);

  useEffect(() => {
    if (fromDate && toDate && (selectedAssociateId || selectedRoleBasedId || selectedFacilityId)) {
      getJobCompletionTrendData(fromDate, toDate, selectedAssociateId, selectedFacilityId, selectedRoleBasedId);
    }
  }, [fromDate, toDate, selectedAssociateId, selectedRoleBasedId, selectedFacilityId]);

  const canCreateOTJJob = processData?.some((process) => String(process?.processId) === USER_PROCESSES.OTJ);

  return (
    <div>
      {/* <Box
        sx={{
          padding: '16px 240px',
          textAlign: 'center',
          color: 'white',
          fontFamily: 'Manrope',
          backgroundColor: '#006B6B',
        }}>
        <Typography>
          Important Update: Peazy Upgrade Scheduled Thursday, September 19th 8:30am-2pm Eastern. System will be down for
          20 minutes and have reduced functionality during this window of time.
        </Typography>
      </Box> */}
      <HomePageContainer
        sx={{ padding: isProcessEventsLoading ? '6rem 9.75rem 2.5rem 4.75rem' : '0 9.75rem 2.5rem 4.75rem' }}>
        <Box
          sx={{
            display: 'flex',
            padding: '2rem 0rem 1.5rem 0',
            gap: '1.5rem',
            width: '100%',
          }}>
          <SearchFieldContainer>
            <SearchInput type="text" placeholder={t('oneTimeJob:search')} />
            <img src={Icons.SearchIcon} alt={t('altTexts:search')} style={{ width: '1.5rem', height: '1.5rem' }} />
          </SearchFieldContainer>

          {/* Both buttons wait for the process events, so they appear together rather than one at a time. */}
          {!isProcessEventsLoading && hasEventId(processEvents, PROCESS_EVENTS.ServiceRequests) && (
            <Button
              onClick={() => {
                const authInfo = localStorage.getItem('auth');
                const { signedInTime } = JSON.parse(authInfo);
                const isPreviousSignedSessionMoreThan_55_minutes = new Date().getTime() - signedInTime > 1000 * 60 * 55; //checking 55 minutes difference from singed in time.
                if (isPreviousSignedSessionMoreThan_55_minutes) {
                  setIsShowSessionExpirePopUp(true);
                } else {
                  localStorage.removeItem(AsyncStorageKeys.currentJob);
                  navigate('/service-request');
                }
              }}
              icon={<img src={Icons.PlusWhiteIcon} alt={t('altTexts:add')} />}
              type={ButtonType.Primary}
              label={t('homePage:serviceRequest')}
              iconDirection={IconDirection.Right}
            />
          )}
          {!isProcessEventsLoading && (canCreateOTJJob || (role === UserType.Vendor && isDistrictManager)) && (
            <Button
              onClick={() => {
                const authInfo = localStorage.getItem('auth');
                const { signedInTime } = JSON.parse(authInfo);
                const isPreviousSignedSessionMoreThan_55_minutes = new Date().getTime() - signedInTime > 1000 * 60 * 55; //checking 55 minutes difference from singed in time.
                if (isPreviousSignedSessionMoreThan_55_minutes) {
                  setIsShowSessionExpirePopUp(true);
                } else {
                  localStorage.removeItem(AsyncStorageKeys.currentJob);
                  navigate('/createOneTimeJob');
                }
              }}
              icon={<img src={Icons.PlusWhiteIcon} alt={t('altTexts:add')} />}
              type={ButtonType.Primary}
              label={t('homePage:oneTimeJob')}
              iconDirection={IconDirection.Right}
            />
          )}
        </Box>

        {isProcessEventsLoading && (
          <LoaderWrapper>
            <WalkingLoader alt={t('altTexts:loading')} />
          </LoaderWrapper>
        )}

        {!isProcessEventsLoading && (
          <>
            <Box
              sx={{
                boxShadow: '0rem 0.625rem 1.25rem 0.375rem rgba(121, 87, 0, 0.10)',
                backgroundColor: 'white',
                overflow: 'hidden',
                borderRadius: 3,
                marginBottom: 3,
                borderColor: '10px solid green',
              }}>
              <GoogleMap
                allData={allData}
                loading={loading}
                error={error}
                fetchData={handleFetchData}
                showNoDataScreen={showNoDataScreen}
                locationData={locationData}
                processEvents={processEvents}
                isCustomerOrNonVelocitiUsersLoggedIn={isCustomerOrNonVelocitiUsersLoggedIn}
              />
            </Box>

            {(hasEventId(processEvents, PROCESS_EVENTS.JobCompletionTrend) || isCustomerOrNonVelocitiUsersLoggedIn) && (
              <Box
                sx={{
                  marginBottom: 3,
                  borderColor: '10px solid red',
                }}>
                <Box sx={{ marginBottom: '3rem' }}>
                  <GraphTemplateV2
                    graphData={jobCompletionTrendData}
                    id={GraphTypes.CompletionTrend}
                    type={GraphType.MultiBarGraph}
                    loading={isJobCompletionTrendDataLoading}
                    onDateRangeSelect={onDateRangeSelect}
                    handleAssociateChanges={handleAssociateChanges}
                    handleCustomerVendorChanges={handleCustomerVendorChanges}
                    handleLocationChanges={handleFacilityChanges}
                    selectedAssociate={selectedAssociate}
                    selectedCustomerVendor={selectedCustomerVendor}
                    defaultOption={defaultRoleBasedOption}
                    selectedFacility={selectedFacility}
                    isAssociateLoading={isAssociateLoading}
                    isCustomerVendorLoading={isCustomerVendorLoading}
                    isFacilityLoading={isFacilityLoading}
                    labels={labels}
                    dateRangeOptions={dateRangeOptions}
                    defaultDateRangeOption={defaultDateRangeOption}
                    filters={filters}
                    totalCounts={totalCounts}
                    handlePaginationChanges={handlePaginationChanges}
                    currentPages={currentPages}
                    searchString={searchString}
                    setSearchString={handleSearchStringChange}
                  />
                </Box>
                {/* <Box>
            <GraphTemplateV2
              graphData={noShowGraphData}
              id={GraphTypes.NoShows}
              type={GraphType.BarGraph}
              loading={isNoShowsDataLoading}
              onDateRangeSelect={onDateRangeSelect}
              dateRangeOptions={dateRangeOptions}
              defaultDateRangeOption={defaultDateRangeOption}
            />
          </Box> */}
              </Box>
            )}
            {role === UserType.Vendor &&
              (hasEventId(processEvents, PROCESS_EVENTS.JobsToBidOn) || isCustomerOrNonVelocitiUsersLoggedIn) && (
                <Box>
                  <Wrapper>
                    <HeaderWrapper>
                      <Title>{t('dashboard:jobsToBidOn')}</Title>
                      <Link to="/marketplace/all-jobs" style={{ textDecoration: 'none' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', padding: 0, textDecoration: 'none' }}>
                          <ButtonTitle>{t('dashboard:viewAll')}</ButtonTitle>
                          <img src={Icons.ArrowForwardIcon} sx={{ paddingLeft: '0.5rem' }} />
                        </Box>
                      </Link>
                    </HeaderWrapper>
                    <Box
                      sx={{
                        margin: 4,
                        paddingBottom: 3,
                        borderColor: '10px solid green',
                      }}>
                      <AllJobs />
                    </Box>
                  </Wrapper>
                </Box>
              )}

            <ApproveDialog
              title={t('logout:sessionTimedOutTitle')}
              loading={isShowSessionExpirePopUpLoading}
              ChildComponent={<LogoutUI />}
              isApproveWindowOpen={isShowSessionExpirePopUp}
              showCancelButton={false}
              isShowCloseIcon={false}
              handleCloseApproveWindow={() => setIsShowSessionExpirePopUp(false)}
              handleApprove={() => signOut()}
            />
          </>
        )}
      </HomePageContainer>
    </div>
  );
};

export default HomePage;
